import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

export interface ServiceOrderItem {
    documentoItemId: number | null;
    codigo: string;
    descricao: string;
    quantidade: number;
    valorOriginal: number;
    desconto: number;
    total: number;
    flagAprovado: boolean | null;
    flagPendente: boolean | null;
    isProcessing?: boolean;
    actionError?: string;
}

export interface ServiceOrder {
    documentoId: number;
    id: string;
    vehicle: string;
    year: number | null;
    client: string;
    technician: string;
    status: string;
    rawStatus: string;
    issuedAt: string | null;
    itens: ServiceOrderItem[];
}

export interface DiscountItemAction {
    documentoId: number;
    item: ServiceOrderItem;
    approvalOs: boolean;
}

@Component({
    selector: 'app-os-approval-card',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './os-approval-card.component.html',
    styleUrls: ['./os-approval-card.component.scss']
})
export class OsApprovalCardComponent {
    @Input() order!: ServiceOrder;

    @Output() approvalRejectOs = new EventEmitter<DiscountItemAction>();

    approvalOS(item: ServiceOrderItem, approvalOs: boolean): void {
        if (item.isProcessing || !this.canEditApproval(item)) {
            return;
        }

        if (item.flagAprovado === approvalOs) {
            return;
        }
        this.approvalRejectOs.emit({ documentoId: this.order.documentoId, item, approvalOs });
    }

    canEditApproval(item: ServiceOrderItem): boolean {
        return item.flagPendente !== null && this.isOrderOpen;
    }

    get isOrderOpen(): boolean {
        return (this.order.rawStatus || this.order.status || '').trim().toUpperCase() === 'ABERTO';
    }

    formatCurrency(value: number): string {
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    hasItemStatus(item: ServiceOrderItem): boolean {
        return item.flagPendente !== null;
    }

    itemStatusLabel(item: ServiceOrderItem): string {
        if (item.flagPendente) {
            return 'PENDENTE';
        }

        return item.flagAprovado ? 'APROVADO' : 'REPROVADO';
    }

    itemStatusClass(item: ServiceOrderItem): string {
        if (item.flagPendente) {
            return 'item-status-pending';
        }

        return item.flagAprovado ? 'item-status-approved' : 'item-status-rejected';
    }

    get displayId(): string {
        return this.formatPlate(this.order.id);
    }

    get displayIssuedAt(): string {
        if (!this.order.issuedAt) {
            return '';
        }

        const date = new Date(this.order.issuedAt);
        if (Number.isNaN(date.getTime())) {
            return '';
        }

        return new Intl.DateTimeFormat('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        }).format(date);
    }

    getStatusClass(status: string): string {
        const normalizedStatus = (status || '').trim().toUpperCase();

        switch (normalizedStatus) {
            case 'ABERTO':
                return 'status-open';
            case 'AUTORIZADO':
                return 'status-authorized';
            case 'FATURADO':
                return 'status-billed';
            case 'FECHADO':
                return 'status-closed';
            case 'LIBERADO':
                return 'status-released';
            case 'RECUSADO':
                return 'status-refused';
            case 'JUNÇÃO':
            case 'JUNCAO':
                return 'status-merge';
            default:
                if (normalizedStatus.includes('ANDAMENTO')) {
                    return 'status-in-progress';
                }

                if (normalizedStatus.includes('AGUARD')) {
                    return 'status-waiting';
                }

                if (
                    normalizedStatus.includes('PRONTO') ||
                    normalizedStatus.includes('CONCL') ||
                    normalizedStatus.includes('FINALIZ')
                ) {
                    return 'status-ready';
                }

                return 'status-default';
        }
    }

    private formatPlate(value: string): string {
        const compactValue = (value || '').trim().replace(/-/g, '').toUpperCase();
        if (!/^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(compactValue)) {
            return value;
        }

        return `${compactValue.slice(0, 3)}-${compactValue.slice(3)}`;
    }
}
